/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, type MemoExoticComponent, type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import EmptyState from '@atlaskit/empty-state';
import Link from '@atlaskit/link/link';
import { token } from '@atlaskit/tokens';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import ErrorGeneric from '../assets/ErrorGeneric.svg';

import { messages } from './messages';

const styles = cssMap({
	body: {
		paddingInlineStart: token('space.800'),
		paddingInlineEnd: token('space.800'),
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.300'),
	},
});

type Props = {
	icon: ReactNode;
	name: string;
	url: string;
};

export const ErrorFallbackPreviewPanel: MemoExoticComponent<
	({ url, name, icon }: Props) => JSX.Element
> = memo(({ url, name, icon }: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const { close } = usePreviewPanelsActions();

	return (
		<PreviewPanel>
			<PreviewPanelHeader onClose={close} url={url}>
				<PreviewPanelTitle title={name} icon={icon} />
			</PreviewPanelHeader>
			<PreviewPanelBody styles={styles.body}>
				<EmptyState
					header={formatMessage(messages.errorHeader)}
					description={formatMessage(messages.errorDescription, {
						link: (chunks: ReactNode) => <Link href="https://status.atlassian.com/">{chunks}</Link>,
					})}
					headingLevel={2}
					imageUrl={ErrorGeneric}
				/>
			</PreviewPanelBody>
		</PreviewPanel>
	);
});
